import { syncLogs } from "@/lib/interactionLog/sync";
import { useCallback, useEffect, useState } from "react";
import { installAuthInterceptor } from "@/lib/authInterceptor";
import { SESSION_EXPIRED_EVENT, TOKEN_RENEWED_EVENT } from "@/lib/sessionGuard";

const KEY = "vibez-session";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { user: null, token: null, permissions: null };

// Every page talks to the API through the global axios instance, so this is
// where a dead token gets noticed after boot. Installed once per bundle.
installAuthInterceptor();

const read = () => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...EMPTY, ...JSON.parse(raw) } : EMPTY;
  } catch {
    return EMPTY;
  }
};

// Client-side JWT expiry check. The server is the source of truth (it 401s on
// every call), but a forged/revoked token should never render the shell even
// before the first API round-trip — so we drop it the moment we can prove the
// `exp` claim has passed. Does NOT validate the signature (that's the server's
// job); it only refuses tokens that are provably expired offline.
const isExpired = (token) => {
  if (!token || token.split(".").length !== 3) return false; // unreadable → let server decide
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" && payload.exp * 1000 < Date.now();
  } catch {
    return false;
  }
};

// The session must outlive a page refresh: the JWT is valid for 7 days, but
// holding it in React state alone bounced the user back to the login screen on
// every reload.
//
// SECURITY (2026-08-25): persistence alone is NOT proof of a live session. A
// stale, revoked or forged localStorage entry used to render every protected
// screen client-side. On boot with a stored token we call GET /auth/me — 401
// clears the session immediately; 200 rehydrates user/permissions from the
// server's answer rather than trusting what localStorage claimed. /auth/me
// returns the SAME user shape as /auth/login (id, bill_printer_*, …), so the
// replacement loses nothing a screen reads after a reload.
export function useSession() {
  const [session, setSession] = useState(read);

  useEffect(() => {
    // Provably-expired token: don't even render the shell, drop it now.
    if (session.token && isExpired(session.token)) {
      localStorage.removeItem(KEY);
      setSession(EMPTY);
      return undefined;
    }
    if (!session.token) return undefined;
    let live = true;
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${session.token}` } })
      .then(async r => {
        if (!live) return;
        if (r.status === 401) {
          localStorage.removeItem(KEY);
          setSession(EMPTY);
          return;
        }
        if (!r.ok) return; // network hiccup: keep the session, APIs still enforce auth
        const data = await r.json();
        const next = {
          user: {
            ...data.user,
            // `landing` is the per-role home the router falls back to; it lives
            // beside `user` in the auth payloads, so it is folded in here.
            landing: data.landing || session.user?.landing,
            permissions_version: data.permissions_version,
          },
          token: session.token,
          permissions: Array.isArray(data.permissions) ? data.permissions : session.permissions,
        };
        localStorage.setItem(KEY, JSON.stringify(next));
        setSession(next);
      })
      .catch(() => { /* offline: server enforcement covers us */ });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback((user, accessToken, permissions) => {
    const next = {
      user,
      token: accessToken || null,
      permissions: Array.isArray(permissions) ? permissions : null,
    };
    localStorage.setItem(KEY, JSON.stringify(next));
    setSession(next);
  }, []);

  const logout = useCallback(() => {
    // Upload what this user did before their session goes: the upload reads the token
    // synchronously, so it still carries their name after the key is removed below.
    syncLogs("logout");
    localStorage.removeItem(KEY);
    // The mesh signing key now survives a reload on purpose, so signing out has
    // to take it with the session — otherwise the next captain on this tablet
    // inherits the last one's key and punches under their name.
    import("@/lib/captainMesh/transport/identity")
      .then(m => m.clearActorSession())
      .catch(() => {});
    setSession(EMPTY);
  }, []);

  // sessionGuard reports what the server said about the token: a renewal swaps it in
  // place, a 401 on the current token signs out to the login screen.
  useEffect(() => {
    const onRenewed = e => setSession(s => (s.token && e.detail?.token ? { ...s, token: e.detail.token } : s));
    // Another tab renewed or signed out: follow it, or this tab keeps a token
    // nothing will ever renew and 401-loops once it expires.
    const onStorage = e => {
      if (e.key !== KEY) return;
      if (!e.newValue) return setSession(EMPTY);
      try {
        const token = JSON.parse(e.newValue)?.token;
        if (token) setSession(s => (s.token && s.token !== token ? { ...s, token } : s));
      } catch {
        /* unreadable entry: leave this tab as it is */
      }
    };
    window.addEventListener(TOKEN_RENEWED_EVENT, onRenewed);
    window.addEventListener(SESSION_EXPIRED_EVENT, logout);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(TOKEN_RENEWED_EVENT, onRenewed);
      window.removeEventListener(SESSION_EXPIRED_EVENT, logout);
      window.removeEventListener("storage", onStorage);
    };
  }, [logout]);

  // A change the person made to themselves (their screen language), saved by the
  // server first, then kept here so the screens follow without a reload.
  const patchUser = useCallback(patch => {
    setSession(s => {
      if (!s.user) return s;
      const next = { ...s, user: { ...s.user, ...patch } };
      localStorage.setItem(KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  return { user: session.user, token: session.token, permissions: session.permissions, login, logout, patchUser };
}
